import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import dayjs from 'dayjs'

import { useUGCFunnelApi } from '@/common/api/modules/ugcFunnel'
import { useUGCNextActionsApi } from '@/common/api/modules/ugcNextActions'
import { NextActionSeverity } from '@/common/api/params/ugcNextActions'
import { ugcMailPath } from '@/common/app/path'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import IconText from '@/components/view/atoms/icon/IconText'
import SimplePager from '@/components/view/atoms/pager/SimplePager'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import GroupButton from '@/components/view/atoms/button/GroupButton'
import MessageBar, { MessageType } from '@/components/view/molecules/bar/MessageBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

/**
 * UGC のダッシュボード（ファネル型）。
 *
 * 「集める → ためる・出す → 効く」を1本の流れで見せる。表現は
 * AIレコメンドのダッシュボード（レコメンド成果）と揃えている——
 * 月送りのページャ・段の間の矢印・太字24pxの数値＋単位。
 * 同じ製品の中で段階指標の見た目が画面ごとに違うと、読み方を
 * 画面ごとに覚え直すことになる。
 *
 * 率の母数は段ごとに違う（開封率の母数は送信数、投稿率の母数は依頼数）。
 * 母数が0の率は「—」で出す。0% と「まだ測れていない」は意味が違う。
 */

/** 「今日やるべきこと」の重要度の見せ方。至急 → 要対応 → 推奨 の3段だけに絞る */
const SEVERITY_STYLE: Record<NextActionSeverity, { label: string; className: string }> = {
  critical: { label: '至急', className: 'bg-surface-critical text-critical' },
  warning: { label: '要対応', className: 'bg-surface-caution text-caution' },
  info: { label: '推奨', className: 'bg-surface-info text-info' }
}

/** レコメンド成果と同じ、段の間の矢印 */
const TransitionSvg = () => {
  return (
    <svg
      width='10'
      height='81'
      viewBox='0 0 10 81'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className='shrink-0'
    >
      <path d='M1.5 16L1.5 65' stroke='#3F4EB0' />
      <g filter='url(#filter0_d_ugc_funnel)'>
        <path d='M1.41464e-06 49L0 33L8 41L1.41464e-06 49Z' fill='white' />
      </g>
      <defs>
        <filter
          id='filter0_d_ugc_funnel'
          x='0'
          y='33'
          width='10'
          height='18'
          filterUnits='userSpaceOnUse'
          colorInterpolationFilters='sRGB'
        >
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feColorMatrix
            in='SourceAlpha'
            type='matrix'
            values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
            result='hardAlpha'
          />
          <feOffset dx='2' />
          <feComposite in2='hardAlpha' operator='out' />
          <feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0' />
          <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_292_25013' />
          <feBlend
            mode='normal'
            in='SourceGraphic'
            in2='effect1_dropShadow_292_25013'
            result='shape'
          />
        </filter>
      </defs>
    </svg>
  )
}

/**
 * 率の表示。**小数第1位まで**（第2位を四捨五入）で揃える。
 * null は「まだ測れていない」なので 0.0 ではなく — を出す。
 */
const rateText = (v: number | null) => (v === null ? '—' : v.toFixed(1))

const StageStep = ({
  label,
  iconType,
  iconColor,
  textColor,
  children
}: {
  label: string
  iconType: IconType
  iconColor?: IconColor
  textColor?: TextColor
  children: React.ReactNode
}) => (
  <div className='flex min-w-[190px] flex-col items-center gap-2'>
    <IconText
      text={label}
      iconType={iconType}
      color={iconColor ?? IconColor.PRIMARY}
      textColor={textColor ?? TextColor.PRIMARY}
      direction='horizon'
      nowrap
    />
    {children}
  </div>
)

/** 期間の単位。行動ログの「日別/週別/月別」と同じ見た目・同じ操作感に揃える */
const SPAN_OPTIONS = [
  { value: 'month', label: '月別' },
  { value: 'fiscal', label: '年度別' }
]

/** 年度（4月〜翌3月）。1〜3月は前年の年度に属する */
const fiscalYearOf = (d: Date) => {
  const dj = dayjs(d)
  return dj.month() + 1 >= 4 ? dj.year() : dj.year() - 1
}

const UGCFunnelDashboardPage = () => {
  const { siteId: siteIdParam } = useParams()
  const siteId = Number(siteIdParam)
  const navigate = useNavigate()
  const [date, setDate] = useState<Date>(new Date())
  // 月表示と年度表示（4月〜翌3月）を切り替えられる
  const [span, setSpan] = useState<'month' | 'fiscal'>('month')
  const month = dayjs(date).format('YYYY-MM')
  const fiscal = fiscalYearOf(date)
  const { data, isLoading, error } = useUGCFunnelApi(
    span === 'month'
      ? { site_id: siteId, days: 30, month }
      : { site_id: siteId, days: 30, fiscal_year: fiscal }
  )
  // やるべきことは7観点ぶんの集計が走る（実測2秒前後）。ファネルが出てから取りに行く。
  // vars が undefined の間は SWR が動かない性質を使って遅延させている
  const { data: actions, error: actionsError } = useUGCNextActionsApi(
    data || error ? { site_id: siteId, days: 30 } : undefined
  )
  const currentFiscal = fiscalYearOf(new Date())

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='UGCダッシュボード'
        description='集める → ためる・出す → 効く、の流れで現状を見ます。'
      />

      <Content>
      {/* 今日やるべきこと。各分析画面を開かないと気づけないことだけを、重要度順に出す。
          確認待ちレビューの警告バーもここへ統合した——同じことを2か所で言うと、
          どちらを見て判断すればよいのか分からなくなる */}
      <Area>
        <Heading subText='効果・分析の状況から、いま手を打てることを挙げています。'>
          今日やるべきこと
        </Heading>
        <AreaContent>
          {actionsError ? (
            <Text size={13} color={TextColor.SECONDARY}>
              状況を集計できませんでした。時間をおいて再度お試しください。
            </Text>
          ) : !actions ? (
            <Text size={13} color={TextColor.SECONDARY}>集計中...</Text>
          ) : actions.all_clear ? (
            <Text size={13} color={TextColor.SECONDARY}>
              いま対応が必要な項目はありません（{actions.checked} 観点を確認しました）。
            </Text>
          ) : (
            <div className='flex flex-col gap-4'>
              {actions.actions.map((a) => {
                const style = SEVERITY_STYLE[a.severity] ?? SEVERITY_STYLE.info
                return (
                  <div key={a.key} className='flex flex-wrap items-start gap-3'>
                    <span
                      className={`${style.className} text-size-10 mt-0.5 shrink-0 rounded-2xl px-2 py-0.5 font-bold`}
                    >
                      {style.label}
                    </span>
                    <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                      <Text size={13} weight='bold'>
                        {a.title}
                      </Text>
                      <Text size={12} color={TextColor.SECONDARY}>
                        {a.detail}
                      </Text>
                    </div>
                    <Button
                      color={ButtonColor.SECONDARY}
                      onClick={() => navigate(`/sites/${siteId}/${a.link}`)}
                    >
                      対応する
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
        </AreaContent>
      </Area>

      {/* 取得に失敗したら黙って読み込み中を出し続けない。
          SWR は 401/404/422 を再試行しないため、握りつぶすと画面が永久に止まる */}
      {error ? (
        <MessageBar type={MessageType.CRITICAL}>
          データを取得できませんでした。時間をおいて再度お試しいただき、続く場合は管理者へお知らせください。
        </MessageBar>
      ) : isLoading || !data ? (
        <Area>
          <AreaContent>
            <Text color={TextColor.SECONDARY}>読み込み中...</Text>
          </AreaContent>
        </Area>
      ) : (
        <>
          <div>
            <SummaryHeading>
              <ItemAlign justify='between'>
                集める（レビュー依頼）
                <ItemAlign gapX={8} nowrap>
                  <GroupButton
                    choices={SPAN_OPTIONS}
                    value={span}
                    onSelect={(v) => setSpan(v as 'month' | 'fiscal')}
                  />
                  {span === 'month' ? (
                    <SimplePager
                      value={dayjs(date).format('YYYY年M月')}
                      prev={'先月'}
                      next={'来月'}
                      onClickPrev={() => setDate((d) => dayjs(d).subtract(1, 'month').toDate())}
                      onClickNext={
                        dayjs().startOf('month').isAfter(dayjs(date).startOf('month'))
                          ? () => setDate((d) => dayjs(d).add(1, 'month').toDate())
                          : undefined
                      }
                    />
                  ) : (
                    <SimplePager
                      value={`${fiscal}年度（${fiscal}/4〜${fiscal + 1}/3）`}
                      prev={'前年度'}
                      next={'翌年度'}
                      onClickPrev={() => setDate((d) => dayjs(d).subtract(1, 'year').toDate())}
                      onClickNext={
                        fiscal < currentFiscal
                          ? () => setDate((d) => dayjs(d).add(1, 'year').toDate())
                          : undefined
                      }
                    />
                  )}
                </ItemAlign>
              </ItemAlign>
            </SummaryHeading>
            <Area noBorder>
              <SummaryItemList>
                <SummaryItem>
                  <Text weight='bold'>依頼を作成</Text>
                  <ItemAlign>
                    <Text weight='bold' size={24}>
                      {data.collect.requests.toLocaleString()}
                    </Text>
                    <Text size={14}>件</Text>
                  </ItemAlign>
                </SummaryItem>
                <SummaryItem>
                  <ItemAlign align='center' gapX={16} nowrap justify='center'>
                    <StageStep label='送信' iconType={IconType.WEB_PAGE}>
                      <ItemAlign gapX={4} nowrap>
                        <Text weight='bold' size={24} nowrap>
                          {data.collect.sent.toLocaleString()}
                        </Text>
                        <Text>件</Text>
                      </ItemAlign>
                    </StageStep>
                    <TransitionSvg />
                    <StageStep label='開封 / 開封率' iconType={IconType.MAIL}>
                      <ItemAlign gapX={4} nowrap>
                        <Text weight='bold' size={24} nowrap>
                          {data.collect.opened.toLocaleString()}
                        </Text>
                        <Text>件 / </Text>
                        <Text weight='bold' size={24} nowrap>
                          {rateText(data.collect.open_rate)}
                        </Text>
                        <Text>%</Text>
                      </ItemAlign>
                    </StageStep>
                    <TransitionSvg />
                    <StageStep label='クリック / クリック率' iconType={IconType.MOUSE_POINTER}>
                      <ItemAlign gapX={4} nowrap>
                        <Text weight='bold' size={24} nowrap>
                          {data.collect.clicked.toLocaleString()}
                        </Text>
                        <Text>件 / </Text>
                        <Text weight='bold' size={24} nowrap>
                          {rateText(data.collect.click_rate)}
                        </Text>
                        <Text>%</Text>
                      </ItemAlign>
                    </StageStep>
                    <TransitionSvg />
                    <StageStep
                      label='投稿 / 投稿率'
                      iconType={IconType.INBOX}
                      iconColor={IconColor.CRITICAL}
                      textColor={TextColor.CRITICAL}
                    >
                      <ItemAlign gapX={4} nowrap>
                        <Text weight='bold' size={24} nowrap>
                          {data.collect.submitted.toLocaleString()}
                        </Text>
                        <Text>件 / </Text>
                        <Text weight='bold' size={24} nowrap>
                          {rateText(data.collect.submit_rate)}
                        </Text>
                        <Text>%</Text>
                      </ItemAlign>
                    </StageStep>
                  </ItemAlign>
                </SummaryItem>
              </SummaryItemList>
              {data.collect.requests === 0 && (
                <div className='mt-3 px-4 pb-2'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    この月の依頼はありません。
                    <Link to={ugcMailPath(siteId)} className='text-link underline'>
                      レビュー依頼メール
                    </Link>
                    の送信モードが「送らない」のままだと、依頼は作られません。
                  </Text>
                </div>
              )}
            </Area>
          </div>

          <div>
            <SummaryHeading>ためる・出す（公開）</SummaryHeading>
            <Area noBorder>
              <SummaryItemList>
                <SummaryItem>
                  <Text weight='bold'>新着レビュー</Text>
                  <ItemAlign>
                    <Text weight='bold' size={24}>
                      {data.curate.reviews.toLocaleString()}
                    </Text>
                    <Text size={14}>件</Text>
                  </ItemAlign>
                </SummaryItem>
                <SummaryItem>
                  <Text weight='bold'>公開 / 公開率</Text>
                  <ItemAlign gapX={4} nowrap>
                    <Text weight='bold' size={24} nowrap>
                      {data.curate.published.toLocaleString()}
                    </Text>
                    <Text>件 / </Text>
                    <Text weight='bold' size={24} nowrap>
                      {rateText(data.curate.publish_rate)}
                    </Text>
                    <Text>%</Text>
                  </ItemAlign>
                </SummaryItem>
                <SummaryItem>
                  <Text weight='bold'>確認待ち（全期間）</Text>
                  <ItemAlign>
                    <Text weight='bold' size={24}>
                      {data.curate.awaiting.toLocaleString()}
                    </Text>
                    <Text size={14}>件</Text>
                  </ItemAlign>
                </SummaryItem>
              </SummaryItemList>
            </Area>
          </div>

          <div>
            <SummaryHeading>効く（サイト上での効果）</SummaryHeading>
            <Area noBorder>
              <SummaryItemList>
                <SummaryItem>
                  <ItemAlign align='center' gapX={16} nowrap justify='center'>
                    <StageStep label='表示' iconType={IconType.WEB_PAGE}>
                      <ItemAlign gapX={4} nowrap>
                        <Text weight='bold' size={24} nowrap>
                          {data.impact.impressions.toLocaleString()}
                        </Text>
                        <Text>件</Text>
                      </ItemAlign>
                    </StageStep>
                    <TransitionSvg />
                    <StageStep label='クリック / クリック率' iconType={IconType.MOUSE_POINTER}>
                      <ItemAlign gapX={4} nowrap>
                        <Text weight='bold' size={24} nowrap>
                          {data.impact.clicks.toLocaleString()}
                        </Text>
                        <Text>件 / </Text>
                        <Text weight='bold' size={24} nowrap>
                          {rateText(data.impact.ctr)}
                        </Text>
                        <Text>%</Text>
                      </ItemAlign>
                    </StageStep>
                    <TransitionSvg />
                    <StageStep
                      label='CV（購入） / CVR'
                      iconType={IconType.INBOX}
                      iconColor={IconColor.CRITICAL}
                      textColor={TextColor.CRITICAL}
                    >
                      <ItemAlign gapX={4} nowrap>
                        <Text weight='bold' size={24} nowrap>
                          {data.impact.purchases.toLocaleString()}
                        </Text>
                        <Text>件 / </Text>
                        <Text weight='bold' size={24} nowrap>
                          {rateText(data.impact.cvr)}
                        </Text>
                        <Text>%</Text>
                      </ItemAlign>
                    </StageStep>
                    <TransitionSvg />
                    <StageStep
                      label='売上高'
                      iconType={IconType.INBOX}
                      iconColor={IconColor.CRITICAL}
                      textColor={TextColor.CRITICAL}
                    >
                      <ItemAlign gapX={4} nowrap>
                        <Text>¥</Text>
                        <Text weight='bold' size={24} nowrap>
                          {Math.round(data.impact.revenue).toLocaleString()}
                        </Text>
                      </ItemAlign>
                    </StageStep>
                  </ItemAlign>
                </SummaryItem>
              </SummaryItemList>
              {data.impact.impressions === 0 && (
                <div className='mt-3 px-4 pb-2'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    この月、UGCの表示が計測されていません。配信タグが商品ページに入っているかを
                    「配信タグ設定」で確認してください。
                  </Text>
                </div>
              )}
            </Area>
          </div>
        </>
      )}
      </Content>
    </Main>
  )
}

export default UGCFunnelDashboardPage
